import { useState, useEffect, useCallback, useMemo } from "react";
import { useFetchProfile } from "@/features/profiling/hooks/useProfile";

const getContactId = (p) => p?.contact_id ?? p?.id;

const useProfileNavigation = (profiles = [], options = {}) => {
  const {
    idParam = "profile",
    updateUrl = true,
    onProfileChange = null,
    userId,
    campaignId = null,
  } = options;

  const [selectedId, setSelectedId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: currentProfile,
    isLoading,
    refetch,
  } = useFetchProfile({
    contactId: selectedId,
    campaignId,
    userId: userId ?? "",
  });

  const allProfileIds = useMemo(
    () => profiles?.map((p) => getContactId(p)) ?? [],
    [profiles],
  );

  useEffect(() => {
    if (profiles.length === 0) return;

    const urlParams = new URLSearchParams(window.location.search);
    const profileId = urlParams.get(idParam);

    if (
      profileId &&
      profiles?.some((p) => getContactId(p) === profileId) &&
      !isModalOpen
    ) {
      setSelectedId(profileId);
      setIsModalOpen(true);
    }
  }, [profiles, idParam, isModalOpen]);

  useEffect(() => {
    if (selectedId && onProfileChange) {
      onProfileChange(selectedId);
    }
  }, [selectedId, onProfileChange]);

  const fetchProfileData = useCallback(
    async (contactId) => {
      if (contactId !== selectedId) return currentProfile;
      const result = await refetch();
      return result.data;
    },
    [selectedId, currentProfile, refetch],
  );

  const openProfile = useCallback(
    (id) => {
      if (!id || !profiles.some((p) => getContactId(p) === id)) return;

      setSelectedId(id);
      setIsModalOpen(true);

      if (updateUrl) {
        const params = new URLSearchParams(window.location.search);
        params.set(idParam, id);
        const query = params.toString();
        window.history.pushState(null, "", query ? `?${query}` : window.location.pathname);
      }
    },
    [profiles, updateUrl, idParam],
  );

  const closeProfile = useCallback(() => {
    setIsModalOpen(false);

    if (updateUrl) {
      const params = new URLSearchParams(window.location.search);
      params.delete(idParam);
      const query = params.toString();
      window.history.pushState(null, "", query ? `?${query}` : window.location.pathname);
    }
  }, [updateUrl]);

  const goToNextProfile = useCallback(() => {
    const currentIndex = allProfileIds.indexOf(selectedId);
    if (currentIndex < allProfileIds.length - 1) {
      const nextId = allProfileIds[currentIndex + 1];
      openProfile(nextId);
      return nextId;
    }
    return null;
  }, [allProfileIds, selectedId, openProfile]);

  const goToPreviousProfile = useCallback(() => {
    const currentIndex = allProfileIds.indexOf(selectedId);
    if (currentIndex > 0) {
      const prevId = allProfileIds[currentIndex - 1];
      openProfile(prevId);
      return prevId;
    }
    return null;
  }, [allProfileIds, selectedId, openProfile]);

  const handleProfileNavigation = useCallback(
    (newId) => {
      if (newId && profiles.some((p) => getContactId(p) === newId)) {
        openProfile(newId);
        setTimeout(() => {
          document
            .getElementById(newId)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);

        return true;
      }
      return false;
    },
    [profiles, openProfile],
  );

  const currentIndex = allProfileIds.indexOf(selectedId);
  const navigationContext = {
    totalCount: allProfileIds.length,
    currentIndex: currentIndex !== -1 ? currentIndex : null,
    currentNumber: currentIndex !== -1 ? currentIndex + 1 : null,
    hasPrevious: currentIndex > 0,
    hasNext: currentIndex < allProfileIds.length - 1 && currentIndex !== -1,
  };

  return {
    selectedId,
    isModalOpen,
    isLoading,
    currentProfile,
    navigationContext,
    openProfile,
    closeProfile,
    goToNextProfile,
    goToPreviousProfile,
    handleProfileNavigation,
    fetchProfileData,
    allProfileIds,
  };
};

export default useProfileNavigation;
